import axios from "axios";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "https://bksaree.sareehouse.shop/api/admin";
const BACKEND_ORIGIN = API_BASE_URL.replace(/\/api\/admin\/?$/, "");

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 15000,
  headers: {
    "Content-Type": "application/json",
  },
});

export const formatDate = (dateString) => {
  if (!dateString) return "-";
  const date = new Date(dateString);
  return date.toLocaleString("en-IN", {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

export const formatCurrency = (value) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(Number(value || 0));

export const getImageUrl = (image) => {
  if (typeof image !== "string" || !image) return "";

  if (/^(https?:|data:|blob:)/i.test(image)) return image;

  return `${BACKEND_ORIGIN}${image.startsWith("/") ? image : `/${image}`}`;
};

export const getProfileImage = (profileData) => {
  return getImageUrl(profileData?.profile);
};

const normalizeUploadUrls = (value) => {
  if (Array.isArray(value)) {
    return value.map(normalizeUploadUrls);
  }

  if (!value || typeof value !== "object") return value;

  Object.entries(value).forEach(([key, fieldValue]) => {
    if (key === "image" || key === "profile" || key === "documentImage") {
      value[key] = getImageUrl(fieldValue);
      return;
    }

    if (key === "images" && Array.isArray(fieldValue)) {
      value[key] = fieldValue.map(getImageUrl);
      return;
    }

    value[key] = normalizeUploadUrls(fieldValue);
  });

  return value;
};

apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("adminToken");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error),
);

const isAuthSessionError = (status, message = "") => {
  const normalized = String(message).toLowerCase();

  if (status === 401) return true;

  return (
    normalized.includes("json web token is expired") ||
    normalized.includes("json web token is invalid") ||
    normalized.includes("session expired") ||
    normalized.includes("please login again") ||
    normalized.includes("please login as admin")
  );
};

const clearAdminSession = () => {
  const hadToken = Boolean(localStorage.getItem("adminToken"));
  localStorage.removeItem("adminToken");
  localStorage.removeItem("adminProfile");

  if (hadToken && typeof window !== "undefined") {
    // Let AuthContext react immediately so guarded routes show Sign In.
    window.dispatchEvent(new Event("auth:unauthorized"));
  }
};

apiClient.interceptors.response.use(
  (response) => {
    response.data = normalizeUploadUrls(response.data);
    return response;
  },
  (error) => {
    const payload = error.response?.data;
    const status = error.response?.status;
    const message = payload?.message || error.message || "";

    // Skip auto-logout on login/auth endpoints so wrong password
    // does not clear an existing session mid-flow.
    const requestUrl = String(error.config?.url || "");
    const isAuthEndpoint =
      requestUrl.includes("/login") || requestUrl.endsWith("/logout");

    if (!isAuthEndpoint && isAuthSessionError(status, message)) {
      clearAdminSession();
    }

    return Promise.reject(payload || { message: message || "Request failed" });
  },
);

export const apiRequest = async (endpoint, method, data, headers) => {
  const requestHeaders = { ...(headers || {}) };
  if (typeof FormData !== "undefined" && data instanceof FormData) {
    requestHeaders["Content-Type"] = "multipart/form-data";
  }

  const response = await apiClient({
    url: endpoint,
    method: method || "GET",
    data: data || null,
    headers: requestHeaders,
  });

  return response.data;
};

export default apiClient;
